import type { DifficultyLevel } from "./types";

export interface ConstellationStar {
  /** 0–1 across the sky canvas */
  x: number;
  /** 0–1 down the sky canvas */
  y: number;
}

export interface ConstellationDefinition {
  id: string;
  name: string;
  hint: string;
  stars: ConstellationStar[];
  /** Undirected star index pairs to connect. */
  edges: [number, number][];
  difficulty: DifficultyLevel;
}

const CONSTELLATIONS: ConstellationDefinition[] = [
  {
    id: "triangle",
    name: "Summer Triangle",
    hint: "Connect the three brightest corners.",
    difficulty: "easy",
    stars: [
      { x: 0.5, y: 0.18 },
      { x: 0.22, y: 0.72 },
      { x: 0.78, y: 0.72 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 0],
    ],
  },
  {
    id: "arrow",
    name: "Hunter's Arrow",
    hint: "Draw the shaft, then the arrow head.",
    difficulty: "easy",
    stars: [
      { x: 0.15, y: 0.5 },
      { x: 0.45, y: 0.5 },
      { x: 0.78, y: 0.32 },
      { x: 0.78, y: 0.68 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [1, 3],
    ],
  },
  {
    id: "kite",
    name: "Night Kite",
    hint: "Outline the diamond kite.",
    difficulty: "easy",
    stars: [
      { x: 0.5, y: 0.15 },
      { x: 0.22, y: 0.5 },
      { x: 0.5, y: 0.85 },
      { x: 0.78, y: 0.5 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
    ],
  },
  {
    id: "three-stars",
    name: "Three Stars",
    hint: "Connect the stars in a row.",
    difficulty: "easy",
    stars: [
      { x: 0.15, y: 0.5 },
      { x: 0.5, y: 0.5 },
      { x: 0.85, y: 0.5 },
    ],
    edges: [
      [0, 1],
      [1, 2],
    ],
  },
  {
    id: "sky-square",
    name: "Sky Square",
    hint: "Draw all four sides of the square.",
    difficulty: "easy",
    stars: [
      { x: 0.25, y: 0.25 },
      { x: 0.75, y: 0.25 },
      { x: 0.75, y: 0.75 },
      { x: 0.25, y: 0.75 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
    ],
  },
  {
    id: "little-cup",
    name: "Mini Dipper",
    hint: "Trace the mini scoop.",
    difficulty: "easy",
    stars: [
      { x: 0.2, y: 0.35 },
      { x: 0.4, y: 0.28 },
      { x: 0.6, y: 0.32 },
      { x: 0.75, y: 0.48 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 3],
    ],
  },
  {
    id: "cassiopeia",
    name: "Cassiopeia",
    hint: "Trace the wobbly W in the sky.",
    difficulty: "medium",
    stars: [
      { x: 0.12, y: 0.35 },
      { x: 0.32, y: 0.62 },
      { x: 0.5, y: 0.38 },
      { x: 0.68, y: 0.65 },
      { x: 0.88, y: 0.4 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
    ],
  },
  {
    id: "cross",
    name: "Southern Cross",
    hint: "Connect the cross beams.",
    difficulty: "medium",
    stars: [
      { x: 0.5, y: 0.15 },
      { x: 0.5, y: 0.85 },
      { x: 0.22, y: 0.5 },
      { x: 0.78, y: 0.5 },
      { x: 0.5, y: 0.5 },
    ],
    edges: [
      [0, 1],
      [2, 3],
      [1, 4],
    ],
  },
  {
    id: "lyra",
    name: "Lyra",
    hint: "Connect the harp shape, then the tail star.",
    difficulty: "medium",
    stars: [
      { x: 0.35, y: 0.25 },
      { x: 0.65, y: 0.22 },
      { x: 0.7, y: 0.55 },
      { x: 0.4, y: 0.58 },
      { x: 0.52, y: 0.82 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
      [2, 4],
    ],
  },
  {
    id: "leo",
    name: "Leo",
    hint: "Trace the lion's sickle curve.",
    difficulty: "medium",
    stars: [
      { x: 0.82, y: 0.35 },
      { x: 0.68, y: 0.28 },
      { x: 0.5, y: 0.32 },
      { x: 0.38, y: 0.48 },
      { x: 0.42, y: 0.68 },
      { x: 0.58, y: 0.75 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
      [4, 5],
    ],
  },
  {
    id: "boot",
    name: "Old Boot",
    hint: "Outline the boot from toe to heel.",
    difficulty: "medium",
    stars: [
      { x: 0.2, y: 0.55 },
      { x: 0.45, y: 0.45 },
      { x: 0.7, y: 0.5 },
      { x: 0.75, y: 0.72 },
      { x: 0.35, y: 0.78 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
      [4, 0],
    ],
  },
  {
    id: "big-dipper",
    name: "Big Dipper",
    hint: "Trace the scoop and the handle.",
    difficulty: "hard",
    stars: [
      { x: 0.12, y: 0.28 },
      { x: 0.28, y: 0.22 },
      { x: 0.44, y: 0.26 },
      { x: 0.58, y: 0.38 },
      { x: 0.7, y: 0.55 },
      { x: 0.82, y: 0.7 },
      { x: 0.9, y: 0.82 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
      [3, 4],
      [4, 5],
      [5, 6],
    ],
  },
  {
    id: "orion",
    name: "Orion",
    hint: "Belt first, then shoulders and feet.",
    difficulty: "hard",
    stars: [
      { x: 0.32, y: 0.2 },
      { x: 0.68, y: 0.2 },
      { x: 0.38, y: 0.45 },
      { x: 0.5, y: 0.48 },
      { x: 0.62, y: 0.45 },
      { x: 0.28, y: 0.78 },
      { x: 0.72, y: 0.78 },
    ],
    edges: [
      [0, 1],
      [0, 2],
      [1, 4],
      [2, 3],
      [3, 4],
      [2, 5],
      [4, 6],
    ],
  },
  {
    id: "little-dipper",
    name: "Little Dipper",
    hint: "Full scoop and handle — seven stars.",
    difficulty: "hard",
    stars: [
      { x: 0.78, y: 0.22 },
      { x: 0.65, y: 0.18 },
      { x: 0.5, y: 0.22 },
      { x: 0.38, y: 0.32 },
      { x: 0.28, y: 0.48 },
      { x: 0.18, y: 0.62 },
      { x: 0.12, y: 0.78 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
      [3, 0],
      [4, 5],
      [5, 6],
    ],
  },
  {
    id: "cygnus",
    name: "Cygnus",
    hint: "Northern Cross — body and wide wings.",
    difficulty: "hard",
    stars: [
      { x: 0.5, y: 0.12 },
      { x: 0.5, y: 0.45 },
      { x: 0.5, y: 0.78 },
      { x: 0.22, y: 0.42 },
      { x: 0.78, y: 0.42 },
      { x: 0.35, y: 0.55 },
      { x: 0.65, y: 0.55 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [3, 4],
      [1, 3],
      [1, 4],
      [1, 5],
      [1, 6],
    ],
  },
  {
    id: "draco",
    name: "Draco",
    hint: "Wind through the dragon's body.",
    difficulty: "hard",
    stars: [
      { x: 0.15, y: 0.2 },
      { x: 0.3, y: 0.15 },
      { x: 0.48, y: 0.22 },
      { x: 0.62, y: 0.35 },
      { x: 0.72, y: 0.52 },
      { x: 0.65, y: 0.68 },
      { x: 0.48, y: 0.78 },
      { x: 0.28, y: 0.72 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
      [4, 5],
      [5, 6],
      [6, 7],
    ],
  },
  {
    id: "hercules",
    name: "Hercules",
    hint: "Keystone body, then arms and legs.",
    difficulty: "hard",
    stars: [
      { x: 0.32, y: 0.3 },
      { x: 0.55, y: 0.25 },
      { x: 0.62, y: 0.48 },
      { x: 0.38, y: 0.52 },
      { x: 0.18, y: 0.38 },
      { x: 0.82, y: 0.4 },
      { x: 0.35, y: 0.78 },
      { x: 0.6, y: 0.82 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
      [0, 4],
      [1, 5],
      [3, 6],
      [2, 7],
    ],
  },
  {
    id: "scorpius",
    name: "Scorpius",
    hint: "Head to stinger along the scorpion curve.",
    difficulty: "hard",
    stars: [
      { x: 0.15, y: 0.45 },
      { x: 0.28, y: 0.38 },
      { x: 0.42, y: 0.42 },
      { x: 0.55, y: 0.52 },
      { x: 0.65, y: 0.65 },
      { x: 0.75, y: 0.55 },
      { x: 0.85, y: 0.42 },
      { x: 0.88, y: 0.28 },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4],
      [4, 5],
      [5, 6],
      [6, 7],
    ],
  },
];

export function constellationsForDifficulty(level: DifficultyLevel): ConstellationDefinition[] {
  return CONSTELLATIONS.filter((c) => c.difficulty === level);
}

export function showConstellationVisualHint(level: DifficultyLevel): boolean {
  return level === "easy" || level === "medium";
}

export function pickConstellation(level: DifficultyLevel): ConstellationDefinition {
  const pool = constellationsForDifficulty(level);
  return pool[Math.floor(Math.random() * pool.length)]!;
}

export function edgeKey(a: number, b: number): string {
  return a < b ? `${a}-${b}` : `${b}-${a}`;
}

export function requiredEdgeKeys(edges: [number, number][]): Set<string> {
  return new Set(edges.map(([a, b]) => edgeKey(a, b)));
}

export function isConstellationComplete(
  drawn: Set<string>,
  required: [number, number][],
): boolean {
  const need = requiredEdgeKeys(required);
  if (drawn.size !== need.size) return false;
  for (const key of need) {
    if (!drawn.has(key)) return false;
  }
  return true;
}
